import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronDown, LifeBuoy, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fixed extension categories that drive the side menu
const CATEGORIES = [
  { label: 'COMPONENTS', name: 'Components', slug: 'components' },
  { label: 'MODULES', name: 'Modules', slug: 'modules' },
  { label: 'PLUGINS', name: 'Plugins', slug: 'plugins' },
  { label: 'TEMPLATES', name: 'Templates', slug: 'templates' },
];

const productInCategory = (p, categoryName) => {
  const cats = Array.isArray(p.categories) && p.categories.length > 0
    ? p.categories
    : [p.category].filter(Boolean);
  return cats.some((c) => (c || '').toLowerCase() === categoryName.toLowerCase());
};

const productHref = (p) => (p.seo_url ? `/extensions/${p.seo_url}` : `/product/${p.id}`);

/**
 * Side menu with one accordion per Joomla extension category. Expanding a
 * category reveals the products it contains. Support is a plain link.
 *
 * Props:
 *  - onCategorySelect?: (categoryName | 'All') => void  (also filters the shop grid)
 *  - currentCategory?: string
 */
export const CategoryProductsAccordion = ({ onCategorySelect, currentCategory }) => {
  const [products, setProducts] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set(['Components']));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/store/products`);
        setProducts(Array.isArray(res.data) ? res.data : (res.data.products || []));
      } catch (error) {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Keep the active category open
  useEffect(() => {
    if (currentCategory && currentCategory !== 'All') {
      setExpanded((prev) => new Set(prev).add(currentCategory));
    }
  }, [currentCategory]);

  const toggle = (name) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
    if (onCategorySelect) onCategorySelect(name);
  };

  if (loading) {
    return (
      <div className="w-full animate-pulse space-y-2" data-testid="category-accordion-loading">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-white/5 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="w-full rounded-xl overflow-hidden border border-white/10 bg-[#0f1830]/60 backdrop-blur-sm" data-testid="category-products-accordion">
      {CATEGORIES.map((cat) => {
        const items = products.filter((p) => productInCategory(p, cat.name));
        const isOpen = expanded.has(cat.name);
        const isActive = currentCategory === cat.name;
        return (
          <div key={cat.slug} className="border-b border-white/10 last:border-b-0">
            <button
              onClick={() => toggle(cat.name)}
              className={`w-full flex items-center justify-between px-5 py-4 transition-colors ${
                isActive ? 'bg-blue-600/20 text-blue-300' : 'text-white hover:bg-white/5'
              }`}
              data-testid={`side-cat-${cat.slug}`}
              aria-expanded={isOpen}
            >
              <span className="font-bold text-sm tracking-wider">{cat.label}</span>
              <span className="flex items-center gap-2">
                <span className="text-xs text-gray-400">{items.length}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
              </span>
            </button>

            {isOpen && (
              <div className="bg-black/20" data-testid={`side-cat-panel-${cat.slug}`}>
                {items.length === 0 ? (
                  <span className="block px-6 py-3 text-sm text-gray-500 italic">Coming soon</span>
                ) : (
                  items.map((p) => (
                    <Link
                      key={p.id}
                      to={productHref(p)}
                      className="group flex items-center gap-2 px-6 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-blue-500/10 transition-colors border-l-2 border-transparent hover:border-blue-400"
                      data-testid={`side-product-${p.id}`}
                    >
                      <Package className="w-3.5 h-3.5 text-blue-400/70 group-hover:text-blue-400 flex-shrink-0" />
                      <span className="truncate">{p.name}</span>
                    </Link>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Support link */}
      <Link
        to="/contact"
        className="w-full flex items-center gap-2 px-5 py-4 text-white hover:bg-white/5 transition-colors font-bold text-sm tracking-wider"
        data-testid="side-cat-support"
      >
        <LifeBuoy className="w-4 h-4 text-blue-400" />
        SUPPORT
      </Link>
    </div>
  );
};

export default CategoryProductsAccordion;
